import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { fetchIncentiveTickets, updateTicketStatus } from '../../services/incentiveTicketsService';
import { formatOMR } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_COLORS = {
  'Pending Assignment': 'bg-warning/15 text-warning',
  'Pending Team Assignment': 'bg-warning/15 text-warning',
  'Calculated': 'bg-blue-500/15 text-blue-400',
  'Pending Approval': 'bg-orange-500/15 text-orange-400',
  'Approved': 'bg-success/15 text-success',
  'Ready for Payment': 'bg-purple-500/15 text-purple-400',
  'Paid': 'bg-emerald-500/15 text-emerald-400',
  'Rejected': 'bg-error/15 text-error',
  'On Hold': 'bg-yellow-500/15 text-yellow-400',
};

const ActionModal = ({ isOpen, onClose, onConfirm, title, description, actionLabel, variant = 'default', requiresComment = false, requiresPaymentRef = false, requiresPaymentDate = false }) => {
  const [comment, setComment] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (requiresComment && !comment?.trim()) { setError('Comment is required'); return; }
    if (requiresPaymentRef && !paymentRef?.trim()) { setError('Payment reference is required'); return; }
    setLoading(true);
    setError('');
    try {
      await onConfirm({ comment, paymentRef, paymentDate });
      setComment(''); setPaymentRef(''); setPaymentDate('');
      onClose();
    } catch (err) {
      setError(err?.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-5 border-b border-border">
          <h3 className="font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="p-5 space-y-3">
          {requiresPaymentRef && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Payment Reference *</label>
              <input type="text" value={paymentRef} onChange={e => setPaymentRef(e?.target?.value)} placeholder="e.g., PAY-2026-001"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>
          )}
          {requiresPaymentDate && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Payment Date</label>
              <input type="date" value={paymentDate} onChange={e => setPaymentDate(e?.target?.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Comments {requiresComment ? '*' : '(Optional)'}</label>
            <textarea value={comment} onChange={e => setComment(e?.target?.value)} placeholder="Add any comments..." rows={3}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none" />
          </div>
          {error && <p className="text-xs text-error">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant={variant} onClick={handleConfirm} disabled={loading}>{loading ? 'Processing...' : actionLabel}</Button>
        </div>
      </div>
    </div>
  );
};

const TicketTable = ({ tickets, loading, emptyMessage, navigate, actions = [] }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (tickets?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Icon name="Inbox" size={36} className="text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Ticket #</th>
            <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Project</th>
            <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Client</th>
            <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Net Profit</th>
            <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Total Incentives</th>
            <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Status</th>
            <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Date</th>
            <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {tickets?.map(ticket => (
            <tr key={ticket?.id} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3">
                <button onClick={() => navigate(`/incentive-tickets/${ticket?.id}`)} className="font-mono font-semibold text-primary hover:underline">
                  {ticket?.ticketNumber}
                </button>
              </td>
              <td className="px-4 py-3 font-medium text-foreground max-w-[160px] truncate">{ticket?.projectName}</td>
              <td className="px-4 py-3 text-muted-foreground">{ticket?.clientName || '—'}</td>
              <td className="px-4 py-3 text-right font-medium text-foreground">{formatOMR(ticket?.targetMarginAmount)}</td>
              <td className="px-4 py-3 text-right font-bold text-primary">{formatOMR(ticket?.totalIncentives)}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS?.[ticket?.status] || 'bg-muted text-muted-foreground'}`}>
                  {ticket?.status}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{ticket?.createdAt ? new Date(ticket.createdAt)?.toLocaleDateString('en-GB') : '—'}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-1 flex-wrap">
                  <button onClick={() => navigate(`/incentive-tickets/${ticket?.id}`)} className="p-1.5 hover:bg-primary/10 rounded-lg transition-colors" title="View Details">
                    <Icon name="Eye" size={15} className="text-primary" />
                  </button>
                  {actions?.map(action => (
                    <button key={action?.label} onClick={() => action?.onClick(ticket)} className={`p-1.5 rounded-lg transition-colors text-xs font-medium ${action?.className || 'hover:bg-muted'}`} title={action?.label}>
                      <Icon name={action?.icon} size={15} className={action?.iconClass || 'text-foreground'} />
                    </button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---- Pending Assignment Page ----
export const PendingAssignmentTickets = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = userProfile?.role || user?.profile?.role || 'viewer';
  const canAccess = ['admin', 'project_manager', 'operations']?.includes(userRole);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchIncentiveTickets({ status: ['Pending Team Assignment', 'Pending Assignment'] });
      setTickets(data);
    } catch (err) {
      const msg = err?.message || 'Failed to load tickets';
      setLoadError(msg?.includes('expired') ? 'Your session has expired. Please refresh the page or sign in again.' : msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!canAccess && userRole !== 'viewer') {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Icon name="ShieldOff" size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground">You do not have permission to view this page.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-warning to-orange-500 bg-clip-text text-transparent">Pending Assignment</h1>
            <p className="text-sm text-muted-foreground mt-1">{tickets?.length} tickets awaiting team assignment</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={load}>Refresh</Button>
        </div>
        {loadError && (
          <div className="flex items-center gap-3 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} />{loadError}
            <button onClick={load} className="ml-auto underline text-xs">Retry</button>
          </div>
        )}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <TicketTable tickets={tickets} loading={loading} emptyMessage="No tickets pending assignment" navigate={navigate} />
        </div>
      </div>
    </MainLayout>
  );
};

// ---- Pending Approval Page ----
export const PendingApprovalTickets = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchIncentiveTickets({ status: 'Pending Approval' });
      setTickets(data);
    } catch (err) {
      const msg = err?.message || 'Failed to load tickets';
      setError(msg?.includes('expired') ? 'Your session has expired. Please refresh the page or sign in again.' : msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async ({ comment, paymentRef, paymentDate }) => {
    const { ticketId, newStatus } = actionModal;
    const extraFields = {};
    if (paymentRef) extraFields.payment_reference = paymentRef;
    if (paymentDate) extraFields.payment_date = paymentDate;
    if (comment && newStatus === 'Rejected') extraFields.rejection_reason = comment;
    if (comment && newStatus === 'On Hold') extraFields.hold_reason = comment;
    if (comment && newStatus === 'Pending Team Assignment') extraFields.return_comment = comment;
    if (comment) extraFields.approval_comments = comment;
    await updateTicketStatus(ticketId, newStatus, user?.id, comment, extraFields);
    setSuccessMsg(`Ticket status updated to "${newStatus}"`);
    await load();
  };

  const openAction = (ticket, newStatus, title, description, actionLabel, variant, requiresComment = false, requiresPaymentRef = false) => {
    setActionModal({ ticketId: ticket?.id, newStatus, title, description, actionLabel, variant, requiresComment, requiresPaymentRef });
  };

  const actions = [
    {
      label: 'Approve',
      icon: 'CheckCircle',
      iconClass: 'text-success',
      className: 'hover:bg-success/10',
      onClick: (t) => openAction(t, 'Approved', 'Approve Ticket', `Approve incentive ticket ${t?.ticketNumber}?`, 'Approve', 'default'),
    },
    {
      label: 'Reject',
      icon: 'XCircle',
      iconClass: 'text-error',
      className: 'hover:bg-error/10',
      onClick: (t) => openAction(t, 'Rejected', 'Reject Ticket', `Reject incentive ticket ${t?.ticketNumber}?`, 'Reject', 'destructive', true),
    },
    {
      label: 'Put On Hold',
      icon: 'PauseCircle',
      iconClass: 'text-warning',
      className: 'hover:bg-warning/10',
      onClick: (t) => openAction(t, 'On Hold', 'Put On Hold', `Put ticket ${t?.ticketNumber} on hold?`, 'Put On Hold', 'outline', true),
    },
    {
      label: 'Send Back',
      icon: 'RotateCcw',
      iconClass: 'text-muted-foreground',
      className: 'hover:bg-muted',
      onClick: (t) => openAction(t, 'Pending Team Assignment', 'Send Back for Edit', `Send ticket ${t?.ticketNumber} back for editing?`, 'Send Back', 'outline', true),
    },
  ];

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Pending Approval</h1>
            <p className="text-sm text-muted-foreground mt-1">{tickets?.length} tickets awaiting approval</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={load}>Refresh</Button>
        </div>
        {successMsg && (
          <div className="flex items-center gap-3 p-3 bg-success/10 border border-success/30 rounded-xl text-success text-sm">
            <Icon name="CheckCircle" size={16} />{successMsg}
            <button onClick={() => setSuccessMsg('')} className="ml-auto"><Icon name="X" size={14} /></button>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-3 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} />{error}
            <button onClick={() => setError('')} className="ml-auto"><Icon name="X" size={14} /></button>
          </div>
        )}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <TicketTable tickets={tickets} loading={loading} emptyMessage="No tickets pending approval" navigate={navigate} actions={actions} />
        </div>
        {actionModal && (
          <ActionModal isOpen={true} onClose={() => setActionModal(null)} onConfirm={handleAction}
            title={actionModal?.title} description={actionModal?.description} actionLabel={actionModal?.actionLabel}
            variant={actionModal?.variant} requiresComment={actionModal?.requiresComment} requiresPaymentRef={actionModal?.requiresPaymentRef} />
        )}
      </div>
    </MainLayout>
  );
};

// ---- Approved Tickets Page ----
export const ApprovedTickets = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchIncentiveTickets({ status: 'Approved' });
      setTickets(data);
    } catch (err) {
      const msg = err?.message || 'Failed to load tickets';
      setLoadError(msg?.includes('expired') ? 'Your session has expired. Please refresh the page or sign in again.' : msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async ({ comment, paymentRef, paymentDate }) => {
    const { ticketId, newStatus } = actionModal;
    const extraFields = {};
    if (paymentRef) extraFields.payment_reference = paymentRef;
    if (paymentDate) extraFields.payment_date = paymentDate;
    if (comment) extraFields.approval_comments = comment;
    await updateTicketStatus(ticketId, newStatus, user?.id, comment, extraFields);
    setSuccessMsg(`Ticket status updated to "${newStatus}"`);
    await load();
  };

  const actions = [
    {
      label: 'Mark Ready for Payment',
      icon: 'CreditCard',
      iconClass: 'text-purple-400',
      className: 'hover:bg-purple-500/10',
      onClick: (t) => setActionModal({ ticketId: t?.id, newStatus: 'Ready for Payment', title: 'Mark Ready for Payment', description: `Mark ticket ${t?.ticketNumber} as ready for payment?`, actionLabel: 'Mark Ready', variant: 'default' }),
    },
  ];

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-success to-emerald-600 bg-clip-text text-transparent">Approved Tickets</h1>
            <p className="text-sm text-muted-foreground mt-1">{tickets?.length} approved tickets</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={load}>Refresh</Button>
        </div>
        {successMsg && (
          <div className="flex items-center gap-3 p-3 bg-success/10 border border-success/30 rounded-xl text-success text-sm">
            <Icon name="CheckCircle" size={16} />{successMsg}
            <button onClick={() => setSuccessMsg('')} className="ml-auto"><Icon name="X" size={14} /></button>
          </div>
        )}
        {loadError && (
          <div className="flex items-center gap-3 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} />{loadError}
            <button onClick={load} className="ml-auto underline text-xs">Retry</button>
          </div>
        )}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <TicketTable tickets={tickets} loading={loading} emptyMessage="No approved tickets" navigate={navigate} actions={actions} />
        </div>
        {actionModal && (
          <ActionModal isOpen={true} onClose={() => setActionModal(null)} onConfirm={handleAction}
            title={actionModal?.title} description={actionModal?.description} actionLabel={actionModal?.actionLabel}
            variant={actionModal?.variant} requiresComment={false} requiresPaymentRef={false} />
        )}
      </div>
    </MainLayout>
  );
};

// ---- Ready for Payment Page ----
export const ReadyForPaymentTickets = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchIncentiveTickets({ status: 'Ready for Payment' });
      setTickets(data);
    } catch (err) {
      const msg = err?.message || 'Failed to load tickets';
      setLoadError(msg?.includes('expired') ? 'Your session has expired. Please refresh the page or sign in again.' : msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async ({ comment, paymentRef, paymentDate }) => {
    const { ticketId, newStatus } = actionModal;
    const extraFields = {};
    if (paymentRef) extraFields.payment_reference = paymentRef;
    if (paymentDate) extraFields.payment_date = paymentDate;
    if (comment) extraFields.finance_notes = comment;
    await updateTicketStatus(ticketId, newStatus, user?.id, comment, extraFields);
    setSuccessMsg(`Ticket marked as Paid successfully`);
    await load();
  };

  const actions = [
    {
      label: 'Mark as Paid',
      icon: 'Banknote',
      iconClass: 'text-emerald-400',
      className: 'hover:bg-emerald-500/10',
      onClick: (t) => setActionModal({ ticketId: t?.id, newStatus: 'Paid', title: 'Mark as Paid', description: `Confirm payment for ticket ${t?.ticketNumber}. This will lock the ticket.`, actionLabel: 'Mark as Paid', variant: 'default', requiresPaymentRef: true, requiresPaymentDate: true }),
    },
  ];

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">Ready for Payment</h1>
            <p className="text-sm text-muted-foreground mt-1">{tickets?.length} tickets ready for payment</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={load}>Refresh</Button>
        </div>
        {successMsg && (
          <div className="flex items-center gap-3 p-3 bg-success/10 border border-success/30 rounded-xl text-success text-sm">
            <Icon name="CheckCircle" size={16} />{successMsg}
            <button onClick={() => setSuccessMsg('')} className="ml-auto"><Icon name="X" size={14} /></button>
          </div>
        )}
        {loadError && (
          <div className="flex items-center gap-3 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} />{loadError}
            <button onClick={load} className="ml-auto underline text-xs">Retry</button>
          </div>
        )}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <TicketTable tickets={tickets} loading={loading} emptyMessage="No tickets ready for payment" navigate={navigate} actions={actions} />
        </div>
        {actionModal && (
          <ActionModal isOpen={true} onClose={() => setActionModal(null)} onConfirm={handleAction}
            title={actionModal?.title} description={actionModal?.description} actionLabel={actionModal?.actionLabel}
            variant={actionModal?.variant} requiresComment={false} requiresPaymentRef={actionModal?.requiresPaymentRef} requiresPaymentDate={actionModal?.requiresPaymentDate} />
        )}
      </div>
    </MainLayout>
  );
};

// ---- Paid Tickets Page ----
export const PaidTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchIncentiveTickets({ status: 'Paid' });
      setTickets(data);
    } catch (err) {
      const msg = err?.message || 'Failed to load tickets';
      setLoadError(msg?.includes('expired') ? 'Your session has expired. Please refresh the page or sign in again.' : msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent">Paid Tickets</h1>
            <p className="text-sm text-muted-foreground mt-1">{tickets?.length} paid tickets</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={load}>Refresh</Button>
        </div>
        {loadError && (
          <div className="flex items-center gap-3 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} />{loadError}
            <button onClick={load} className="ml-auto underline text-xs">Retry</button>
          </div>
        )}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <TicketTable tickets={tickets} loading={loading} emptyMessage="No paid tickets yet" navigate={navigate} />
        </div>
      </div>
    </MainLayout>
  );
};

// ---- Rejected Tickets Page ----
export const RejectedTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchIncentiveTickets({ status: 'Rejected' });
      setTickets(data);
    } catch (err) {
      const msg = err?.message || 'Failed to load tickets';
      setLoadError(msg?.includes('expired') ? 'Your session has expired. Please refresh the page or sign in again.' : msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-error to-red-600 bg-clip-text text-transparent">Rejected Tickets</h1>
            <p className="text-sm text-muted-foreground mt-1">{tickets?.length} rejected tickets</p>
          </div>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={load}>Refresh</Button>
        </div>
        {loadError && (
          <div className="flex items-center gap-3 p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} />{loadError}
            <button onClick={load} className="ml-auto underline text-xs">Retry</button>
          </div>
        )}
        <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <TicketTable tickets={tickets} loading={loading} emptyMessage="No rejected tickets" navigate={navigate} />
        </div>
      </div>
    </MainLayout>
  );
};
